import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { notification } from "antd";
import {
  deleteWhatsNewEntry,
  fetchWhatsNewAdminList,
  saveWhatsNewEntry,
  uploadWhatsNewImage,
} from "./whatsNewApi";
import styles from "./whatsNew.module.scss";

const EMPTY = {
  title_en: "",
  title_tr: "",
  summary_en: "",
  summary_tr: "",
  body_en: "",
  body_tr: "",
  product: "platform",
  status: "draft",
  try_it_path: "",
  hero_image_url: "",
  screenshots: [],
};

const WhatsNewCms = () => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [forbidden, setForbidden] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    const result = await fetchWhatsNewAdminList();
    if (result.forbidden) {
      setForbidden(true);
      return;
    }
    setEntries(result.entries || []);
  };

  useEffect(() => {
    reload();
  }, []);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const onUpload = async (file, kind) => {
    const result = await uploadWhatsNewImage(file);
    if (result.error || !result.url) {
      notification.error({ message: t("whats_new.uploadError") });
      return;
    }
    if (kind === "hero") {
      setField("hero_image_url", result.url);
      return;
    }
    setForm((prev) => ({
      ...prev,
      screenshots: [
        ...prev.screenshots,
        { url: result.url, caption_en: "", caption_tr: "" },
      ],
    }));
  };

  const onSave = async () => {
    setSaving(true);
    const result = await saveWhatsNewEntry(form, editingId);
    setSaving(false);
    if (result.error) {
      notification.error({ message: t("whats_new.saveError") });
      return;
    }
    notification.success({ message: t("whats_new.saved") });
    setForm(EMPTY);
    setEditingId(null);
    reload();
  };

  const onEdit = (entry) => {
    setEditingId(entry.id);
    setForm({
      ...EMPTY,
      ...entry,
      screenshots: (entry.screenshots || []).map((shot) => ({
        url: shot.url,
        caption_en: shot.caption_en || "",
        caption_tr: shot.caption_tr || "",
      })),
    });
  };

  const onDelete = async (entryId) => {
    const result = await deleteWhatsNewEntry(entryId);
    if (result.error) {
      notification.error({ message: t("whats_new.saveError") });
      return;
    }
    if (editingId === entryId) {
      setEditingId(null);
      setForm(EMPTY);
    }
    reload();
  };

  if (forbidden) {
    return <div className={styles.forbidden}>{t("whats_new.forbidden")}</div>;
  }

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <h1 className={styles.title}>{t("whats_new.cmsTitle")}</h1>
        <p className={styles.itemMeta}>{t("whats_new.cmsHelp")}</p>
        {entries.map((entry) => (
          <div className={styles.cmsRow} key={entry.id}>
            <div>
              <p className={styles.itemTitle}>{entry.title_en || entry.title}</p>
              <p className={styles.itemMeta}>
                {entry.status} · {entry.product}
              </p>
            </div>
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.secondary}
                onClick={() => onEdit(entry)}
              >
                {t("whats_new.edit")}
              </button>
              <button
                type="button"
                className={styles.secondary}
                onClick={() => onDelete(entry.id)}
              >
                {t("whats_new.delete")}
              </button>
            </div>
          </div>
        ))}

        <form
          className={styles.form}
          onSubmit={(event) => {
            event.preventDefault();
            onSave();
          }}
        >
          <label>
            {t("whats_new.fieldTitleEn")}
            <input
              value={form.title_en}
              onChange={(e) => setField("title_en", e.target.value)}
              required
            />
          </label>
          <label>
            {t("whats_new.fieldTitleTr")}
            <input
              value={form.title_tr}
              onChange={(e) => setField("title_tr", e.target.value)}
            />
          </label>
          <label>
            {t("whats_new.fieldSummaryEn")}
            <textarea
              value={form.summary_en}
              onChange={(e) => setField("summary_en", e.target.value)}
            />
          </label>
          <label>
            {t("whats_new.fieldSummaryTr")}
            <textarea
              value={form.summary_tr}
              onChange={(e) => setField("summary_tr", e.target.value)}
            />
          </label>
          <label>
            {t("whats_new.fieldBodyEn")}
            <textarea
              value={form.body_en}
              onChange={(e) => setField("body_en", e.target.value)}
            />
          </label>
          <label>
            {t("whats_new.fieldBodyTr")}
            <textarea
              value={form.body_tr}
              onChange={(e) => setField("body_tr", e.target.value)}
            />
          </label>
          <label>
            {t("whats_new.fieldProduct")}
            <select
              value={form.product}
              onChange={(e) => setField("product", e.target.value)}
            >
              <option value="platform">platform</option>
              <option value="advisor">advisor</option>
              <option value="prospect">prospect</option>
              <option value="survey">survey</option>
            </select>
          </label>
          <label>
            {t("whats_new.fieldStatus")}
            <select
              value={form.status}
              onChange={(e) => setField("status", e.target.value)}
            >
              <option value="draft">draft</option>
              <option value="published">published</option>
            </select>
          </label>
          <label>
            {t("whats_new.fieldTryIt")}
            <input
              value={form.try_it_path || ""}
              onChange={(e) => setField("try_it_path", e.target.value)}
              placeholder="/console/my-workspace"
            />
          </label>
          <label>
            {t("whats_new.fieldHero")}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onUpload(file, "hero");
                e.target.value = "";
              }}
            />
            {form.hero_image_url ? (
              <img className={styles.preview} src={form.hero_image_url} alt="" />
            ) : null}
          </label>
          <label>
            {t("whats_new.fieldScreenshots")}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onUpload(file, "shot");
                e.target.value = "";
              }}
            />
          </label>
          {(form.screenshots || []).map((shot, index) => (
            <div key={`${shot.url}-${index}`}>
              <img className={styles.preview} src={shot.url} alt="" />
              <input
                placeholder={t("whats_new.captionEn")}
                value={shot.caption_en}
                onChange={(e) => {
                  const next = [...form.screenshots];
                  next[index] = { ...shot, caption_en: e.target.value };
                  setField("screenshots", next);
                }}
              />
              <input
                placeholder={t("whats_new.captionTr")}
                value={shot.caption_tr}
                onChange={(e) => {
                  const next = [...form.screenshots];
                  next[index] = { ...shot, caption_tr: e.target.value };
                  setField("screenshots", next);
                }}
              />
            </div>
          ))}
          <div className={styles.actions}>
            <button type="submit" className={styles.primary} disabled={saving}>
              {t("whats_new.save")}
            </button>
            <button
              type="button"
              className={styles.secondary}
              onClick={() => {
                setForm(EMPTY);
                setEditingId(null);
              }}
            >
              {t("whats_new.new")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WhatsNewCms;
